/**
 * Storefront v6 — the hero (one photo, the words on it), the fulfilment card
 * that overlaps its bottom edge, and the sticky category rail.
 */
import React, { useState } from 'react';
import { MapPin, ShoppingBag, Bike, Clock, Tag } from 'lucide-react';
import { AddressAutocomplete } from '../../../ui/address-autocomplete';
import { OrderNowButton, GhostButton, StatusPill, Chip, money } from '../primitives';
import { runDeliveryCheck, describeDeliveryResult } from '../data/deliveryCheck';

/** The open/closed facts the status pill reads, from the public settings feed. */
export const storeStatusFrom = (settings) => {
  if (!settings || !settings.store_hours_enabled) return null;
  return {
    is_open: settings.is_open !== false,
    opens_time: settings.next_open?.time || settings.next_open?.label || null,
    closes_time: settings.closes_time || null,
  };
};

export const Hero = ({
  brand, theme, photo, title, emphasis, subtitle, ctas, status, headerH = 72, heroRef, onPrimary, onSecondary,
}) => {
  const hasPhoto = !!photo;
  return (
    <section
      ref={heroRef}
      className={`s6-hero s6-on-photo ${hasPhoto ? '' : 's6-hero-fallback'}`}
      style={{ marginTop: -headerH }}
      data-testid="s6-hero"
    >
      {hasPhoto && <img src={photo} alt="" className="s6-hero-photo" fetchPriority="high" data-testid="s6-hero-photo" />}
      <div className="s6-hero-inner s6-container s6-hero-text" style={{ paddingTop: headerH + 32 }}>
        <div className="max-w-2xl">
          {status && <div className="mb-4"><StatusPill status={status} /></div>}
          <h1 className="s6-display" data-testid="s6-hero-title">
            {title}{emphasis ? ' ' : ''}
            {emphasis && <span className={theme.accentStyle === 'script' ? 's6-script s6-emphasis' : 's6-emphasis'} data-testid="s6-hero-emphasis">{emphasis}</span>}
          </h1>
          {subtitle && <p className="mt-4 text-[17px] md:text-lg leading-relaxed max-w-xl" style={{ color: 'rgba(255,255,255,0.86)' }} data-testid="s6-hero-sub">{subtitle}</p>}
          <div className="mt-7 flex flex-wrap gap-3">
            <OrderNowButton onClick={onPrimary} data-testid="s6-hero-primary">{ctas.primary}</OrderNowButton>
            {ctas.secondary && <GhostButton onClick={onSecondary} data-testid="s6-hero-secondary">{ctas.secondary}</GhostButton>}
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * Pickup | Delivery, the address check, and the facts line — everything the
 * customer wants to know before choosing a dish. Facts render only from
 * real data (minimum order, prep time, pickup address).
 */
export const FulfilmentCard = ({
  brand, settings, subdomain, storeSlug, isPreview, pickupAvailable, deliveryAvailable, onOrder, currency, overlap = true,
}) => {
  const [mode, setMode] = useState(deliveryAvailable ? 'delivery' : 'pickup');
  const [query, setQuery] = useState('');
  const [place, setPlace] = useState(null);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);

  const check = async () => {
    if (checking || !query.trim()) return;
    setChecking(true);
    setResult(null);
    try {
      setResult(await runDeliveryCheck({ query, place, storeSlug, subdomain, isPreview }));
    } catch (_e) {
      setResult({ error: true });
    } finally {
      setChecking(false);
    }
  };
  const verdict = describeDeliveryResult(result, { success: brand.delivery_checker_success_text, failure: brand.delivery_checker_failure_text });
  const minOrder = Number(settings?.minimum_order_value) > 0 ? money(settings.minimum_order_value, currency) : null;
  const prep = Number(settings?.order_prep_time_minutes) > 0 ? `${settings.order_prep_time_minutes} min` : null;
  const pickupAddress = settings?.pickup_address || null;
  const facts = [
    minOrder && { Icon: Tag, text: `Minimum order ${minOrder}` },
    prep && { Icon: Clock, text: `Ready in ${prep}` },
    mode === 'pickup' && pickupAddress && { Icon: MapPin, text: pickupAddress },
  ].filter(Boolean);
  if (!pickupAvailable && !deliveryAvailable && !facts.length) return null;

  return (
    <div id="fulfilment" className={`s6-container ${overlap ? 's6-fulfilment' : 's6-fulfilment-flat'}`} data-testid="s6-fulfilment">
      <div className="s6-card p-4 md:p-6 space-y-4">
        {(pickupAvailable || deliveryAvailable) && (
          <div className="s6-segment" role="group" aria-label="How would you like your order">
            {pickupAvailable && (
              <button type="button" aria-pressed={mode === 'pickup' ? 'true' : 'false'} onClick={() => setMode('pickup')} data-testid="s6-fulfilment-pickup">
                <ShoppingBag className="w-4 h-4" /> Pickup
              </button>
            )}
            {deliveryAvailable && (
              <button type="button" aria-pressed={mode === 'delivery' ? 'true' : 'false'} onClick={() => setMode('delivery')} data-testid="s6-fulfilment-delivery">
                <Bike className="w-4 h-4" /> Delivery
              </button>
            )}
          </div>
        )}
        {mode === 'delivery' && deliveryAvailable && (
          <div>
            <div className="s6-input-pill" onKeyDown={(e) => { if (e.key === 'Enter' && !e.defaultPrevented) check(); }} data-testid="s6-checker">
              <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--s6-primary)' }} aria-hidden="true" />
              <AddressAutocomplete
                value={query}
                onChange={(t) => { setQuery(t); setPlace(null); }}
                onSelect={(p) => { setQuery(p.address || p.display_name || ''); setPlace(p); }}
                placeholder="Enter your postal code or address"
                className="h-auto border-0 bg-transparent px-0 py-2 text-[15px] shadow-none focus-visible:ring-0"
              />
              <button type="button" onClick={check} disabled={checking || !query.trim()} className="s6-btn s6-btn-order !min-h-[40px] !px-4 text-sm" data-testid="s6-checker-submit">
                {checking ? 'Checking…' : (brand.delivery_checker_cta || 'Check')}
              </button>
            </div>
            {verdict && (
              <p className="mt-3 text-sm flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="s6-checker-result" data-tone={verdict.tone}>
                <span className="font-semibold" style={{ color: verdict.tone === 'ok' ? 'var(--s6-open)' : verdict.tone === 'no' ? 'var(--s6-closed)' : 'var(--s6-warn)' }}>{verdict.text}</span>
                {verdict.detail && <span style={{ color: 'var(--s6-ink-muted)' }}>{verdict.detail}</span>}
                {verdict.tone === 'ok' && onOrder && <button type="button" onClick={onOrder} className="s6-btn s6-btn-inline">Order now</button>}
              </p>
            )}
          </div>
        )}
        {facts.length > 0 && (
          <div className="s6-facts" data-testid="s6-facts">
            {facts.map(({ Icon, text }) => <span key={text}><Icon className="w-4 h-4" aria-hidden="true" />{text}</span>)}
          </div>
        )}
      </div>
    </div>
  );
};

/** Sticky category chips; `categories` = [{ key, label, count, photo }]. */
export const CategoryRail = ({ categories = [], active, onPick, top = 72 }) => {
  if (!categories.length) return null;
  return (
    <div className="s6-rail" style={{ top }} data-testid="s6-categories">
      <div className="s6-container">
        <div className="s6-strip !pb-0 py-3 -mx-5 px-5 sm:-mx-6 sm:px-6 md:mx-0 md:px-0 gap-2">
          {categories.map((c) => (
            <Chip key={c.key} active={active === c.key} photo={c.photo} count={c.count} onClick={() => onPick(c.key)} data-testid={`s6-category-${c.key}`}>
              {c.label}
            </Chip>
          ))}
        </div>
      </div>
    </div>
  );
};
